import { useNavigate } from 'react-router-dom';

import { PATHS } from 'router/routes';
import { useUserData } from 'context/UserDataProvider';
import {
    Text,
    Stack,
    useToast,
    Spinner,
    useRadioGroup,
    Flex,
    Button,
    RadioGroup,
    Radio,
} from '@chakra-ui/react';
import ZaraButton from 'components/custom-components/buttons/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Input from 'components/custom-components/Inputs/Input';
import { getFormatedPath } from 'hooks';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { getPhoneNumbers } from 'graphql/queries/GetPhoneNumbers';
import { CreateAgent } from 'graphql/mutations/CreateAgent';
import { useError } from 'error-provider/ErrorProvider';
import { GetUserAgents } from 'graphql/queries/GetUserAgents';
import { GetMe } from 'graphql/queries/GetMe';
import { variants, PHONE_TYPE } from 'utils/constants';
import CreateAgentFormWrapper from 'pages/create-agent/components/CreateAgentFormWrapper';
import RadioCard from './RadioCard';
import { InfoIcon } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import { digitPattern } from 'utils';
import { Countries } from './schema';
import { fields, canadaAndAustraliaFields, options } from './utils';

const PhonePick = () => {

    const toast = useToast();
    const router = useNavigate();
    const { userData, isLoading: userDataLoading, fetchUserData } = useUserData();
    const { showError } = useError();
    const apolloClient = useApolloClient();



    const {
        control,
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            country: Countries.US,
            businessAddress: '',
            companyRegisterNumber: '',
            education: '',
            companyWebsite: '',
            legalBusinessName: '',
            phoneNumber: '',
            street: '',
            city: '',
            region: '',
            postalCode: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const { data: meData } = useQuery(GetMe, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { data: agents } = useQuery(GetUserAgents, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [phoneType, setPhoneType] = useState(PHONE_TYPE.LOCAL);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [inputValue, setInputValue] = useState('');


    const { getRadioProps } = useRadioGroup({
        defaultValue: PHONE_TYPE.LOCAL,
        onChange: (phone) => {
            setPhoneType(phone);
            setInputValue('');
            setValue('phoneNumber', '');
            setPhoneNumbers([]);
        },
    });

    const phoneNumber = watch('phoneNumber');

    const { userId } = useMemo(() => {
        const userId = meData?.getMe?.id ?? '';
        return { userId };
    }, [meData]);

    const [onCreateAgent, { loading: isCreateAgentLoading }] = useMutation(CreateAgent, {
        onCompleted(data) {
            if (data.createAgent.success) {
                onUpdateAgentCache();
            } else {
                toast({
                    title: data.createAgent.error,
                    status: 'error',
                    isClosable: true,
                });
            }
        },
        onError(error) {
            showError(undefined, error);
        },
    });

    const [fetchPhones, { loading }] = useLazyQuery(getPhoneNumbers, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { agentId } = useMemo(() => {
        return {
            agentId: agents?.getUserAgents?.agents[0].id,
        };
    }, [agents]);

    const formatedPath = useMemo(() => getFormatedPath(agentId), [agentId]);

    const country = 'US';

    const isSubmitting = isCreateAgentLoading;

    const inputChangeHandler = (event) => {
        const { value } = event.target;
        if (digitPattern.test(value)) {
            setInputValue(value);
            if (value.length === 3) {
                fetchPhones({
                    fetchPolicy: 'network-only',
                    variables: {
                        countryCode: country,
                        numberType: 'local',
                        limit: 10,
                        cityCode: Number(value),
                    },
                    onCompleted(data) {
                        setPhoneNumbers(data.getPhoneNumbers.phoneNumbers);
                    },
                });
            }
        }
    };


    const onUpdateAgentCache = async () => {
        await fetchUserData();
        setTimeout(() => {
            apolloClient.cache.modify({
                id: `UserModelType:${userId}`,
                fields: {
                    isAgentCreated() {
                        return true;
                    },
                    isAllowToStepper() {
                        return {
                            isAllowToStepper: false,
                            has_subscription: true,
                            variant: variants.Default,
                        };
                    },
                },
            });

            apolloClient.cache.modify({
                id: `AgentType:${agentId}`,
                fields: {
                    phoneNumber() {
                        return phoneNumber;
                    },
                },
            });

            router(formatedPath(PATHS.MY_AGENT));

            toast({
                title: 'Welcome to DialZara Dashboard',
                isClosable: true,
                status: 'success',
            });
        }, 2000);
    };



    const onGenerateTollFreeNumbers = () => {
        fetchPhones({
            variables: {
                countryCode: country,
                numberType: 'toll_free',
                limit: 10,
                cityCode: null,
            },
            onCompleted(data) {
                setPhoneNumbers(data.getPhoneNumbers.phoneNumbers);
            },
        });
    };

    const onSubmit = async (data) => {
        await onCreateAgent({
            variables: {
                phoneNumber: data.phoneNumber,
                businessName: data.legalBusinessName,
                address: data.businessAddress,
            },
        });
    };

    useEffect(() => {
        if (errors.phoneNumber?.message) {
            toast({ status: 'error', title: errors.phoneNumber.message });
        }
    }, [errors, toast]);

    const phoneNumbersField = useCallback(
        (label) => {
            if (!loading && inputValue.length >= 3 && phoneNumbers.length === 0) {
                return <Text>No available phone numbers for that area code</Text>;
            }
            if (phoneNumbers.length === 0) {
                return null;
            }
            return (
                <Controller
                    control={control}
                    name='phoneNumber'
                    render={({ field: { onChange, value } }) => (
                        <>
                            <Text fontSize='sm' fontWeight='bold'>
                                {label}
                            </Text>
                            <RadioGroup value={value} onChange={onChange}>
                                <Stack spacing='1' maxH={500} overflow='auto'>
                                    {phoneNumbers.map((number) => (
                                        <Radio key={number.phoneNumber} value={number.phoneNumber}>
                                            {number.phoneNumber}
                                        </Radio>
                                    ))}
                                </Stack>
                            </RadioGroup>
                        </>
                    )}
                />
            );
        },
        [loading, inputValue, control, phoneNumbers]
    );

    useEffect(() => {
        if (!userDataLoading && userData?.agent_info?.phone_number) {
            router(PATHS.MY_AGENT);
        }
    }, [userDataLoading, userData, router]);



    return (
        <CreateAgentFormWrapper
            title='Choose a phone number'
            description={`Select a phone number for your AI receptionist.`}
        >
            <Stack as='form' onSubmit={handleSubmit(onSubmit)} spacing='3'>
                {country === Countries.GB ? (
                    <>
                        {fields.map(({ name, label, placeholder }) => (
                            <div className='space-y-2' key={name}>
                                <Controller
                                    control={control}
                                    name={name}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <div>
                                            <Input
                                                label={label}
                                                placeholder={placeholder}
                                                value={value}
                                                onChange={onChange}
                                                isInvalid={!!error?.message}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        ))}

                        <Stack direction='row' border='1px solid' borderColor='gray.200' borderRadius='md' p='3'>
                            <InfoIcon className='h-5 w-5' />
                            <Stack spacing='0'>
                                <Text fontSize='sm' fontWeight='bold'>
                                    Verification Required
                                </Text>
                                <Text fontSize='sm'>
                                    We'll verify your information and allow you to pick a phone number as soon as
                                    possible (usually within 15 minutes). Please continue to the next step.
                                </Text>
                            </Stack>
                        </Stack>
                    </>
                ) : (
                    <>
                        {country !== Countries.US &&
                            canadaAndAustraliaFields.map(({ name, label, placeholder }) => (
                                <div className='space-y-2' key={name}>
                                    <Controller
                                        control={control}
                                        name={name}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <div>
                                                <Input
                                                    label={label}
                                                    placeholder={placeholder}
                                                    value={value}
                                                    onChange={onChange}
                                                    isInvalid={!!error?.message}
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                            ))}{' '}
                        <>
                            <Stack direction='row' flexWrap='wrap'>
                                {options.map(({ label, icon }) => {
                                    const radio = getRadioProps({ value: label });
                                    return (
                                        <RadioCard key={label} {...radio}>
                                            {icon}
                                            <Text fontSize='sm' fontWeight='bold'>
                                                {' '}
                                                {label}
                                            </Text>
                                        </RadioCard>
                                    );
                                })}
                            </Stack>

                            {phoneType === PHONE_TYPE.LOCAL && (
                                <Stack>
                                    <Input
                                        label='Enter Area Code'
                                        rightIcon={
                                            loading &&
                                            phoneType === PHONE_TYPE.LOCAL &&
                                            inputValue.length && <Spinner color='#3182ce' size='xs' />
                                        }
                                        value={inputValue}
                                        onChange={inputChangeHandler}
                                        placeholder='Enter 3 digit area code'
                                    />

                                    {phoneNumbersField('Select a local number:')}
                                </Stack>
                            )}

                            {phoneType === PHONE_TYPE.TOLL_FREE && (
                                <Stack>
                                    <ZaraButton
                                        variant='blackAlpha'
                                        fontSize='sm'
                                        isLoading={loading}
                                        onClick={onGenerateTollFreeNumbers}
                                    >
                                        Generate Toll-Free Numbers
                                    </ZaraButton>

                                    {phoneNumbersField('Select a toll-free number:')}
                                </Stack>
                            )}
                        </>
                    </>
                )}

                <Flex justifyContent='space-between'>
                    <Button onClick={() => router(PATHS.MY_AGENT)} variant='outline' fontSize='sm'>
                        Back
                    </Button>
                    <ZaraButton variant='blackAlpha' isLoading={isSubmitting} type='submit'>
                        Next
                    </ZaraButton>
                </Flex>
            </Stack>
        </CreateAgentFormWrapper>
    );
};

export default PhonePick;
